import { useState, useEffect, useRef, useCallback } from "react";

const MAX_SHOWS = 10;
const COOLDOWN_MS = 2 * 60 * 1000;
const SHOW_COUNT_KEY = "remix_compare_show_count";
const LAST_SHOWN_KEY = "remix_compare_last_shown";
const LEARNED_KEY = "remix_compare_learned";

const readShowCount = (): number => {
  try {
    const raw = localStorage.getItem(SHOW_COUNT_KEY);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch (e) {
    return 0;
  }
};

const writeShowCount = (n: number) => {
  try {
    localStorage.setItem(SHOW_COUNT_KEY, String(n));
  } catch (e) {}
};

const readLastShown = (): number => {
  try {
    const raw = localStorage.getItem(LAST_SHOWN_KEY);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch (e) {
    return 0;
  }
};

const writeLastShown = (ts: number) => {
  try {
    localStorage.setItem(LAST_SHOWN_KEY, String(ts));
  } catch (e) {}
};

export interface OnboardingState {
  learned: boolean;
  activePoint: { x: number; y: number; dataIndex: number } | null;
  isVisible: boolean;
}

export function useCompareOnboarding(
  isCompareModeActive: boolean,
  chartWrapRef: React.RefObject<HTMLDivElement>,
  chartRef: React.RefObject<any>,
  latestHoverRef: React.MutableRefObject<any>
) {
  const [learned, setLearned] = useState(() => {
    try {
      if (localStorage.getItem(LEARNED_KEY) === "true") return true;
      if (readShowCount() >= MAX_SHOWS) return true;
      return false;
    } catch (e) {
      return false;
    }
  });
  const showCountRef = useRef<number>(readShowCount());
  const lastShownRef = useRef<number>(readLastShown());
  const [activePoint, setActivePoint] = useState<{ x: number; y: number; dataIndex: number } | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMouseRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const isVisibleRef = useRef(false);

  const markLearned = useCallback(() => {
    setLearned(true);
    setIsVisible(false);
    isVisibleRef.current = false;
    setActivePoint(null);
    try {
      localStorage.setItem(LEARNED_KEY, "true");
    } catch (e) {}
  }, []);

  const clearOnboarding = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setIsVisible(false);
    isVisibleRef.current = false;
    setActivePoint(null);
    lastMouseRef.current = null;
  }, []);

  useEffect(() => {
    if (isCompareModeActive) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      setIsVisible(false);
      isVisibleRef.current = false;
      setActivePoint(null);
      // Don't permanently mark learned on first compare — let the user see
      // the tip up to MAX_SHOWS times across sessions. Only stamp last-shown
      // so the cooldown gate still applies.
      const nowTs = Date.now();
      lastShownRef.current = nowTs;
      writeLastShown(nowTs);
    }
  }, [isCompareModeActive]);

  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap || learned || isCompareModeActive) return;

    const onMove = (e: MouseEvent) => {
      const rect = wrap.getBoundingClientRect();
      const ex = e.clientX - rect.left;
      const ey = e.clientY - rect.top;
      const now = Date.now();
      const last = lastMouseRef.current;

      let moved = false;
      if (last) {
        const dist = Math.sqrt(Math.pow(last.x - ex, 2) + Math.pow(last.y - ey, 2));
        if (dist > 10) {
          moved = true;
        }
      }

      if (moved || !last) {
        // reset stationary timer on movement
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        if (isVisibleRef.current) {
          setIsVisible(false);
          isVisibleRef.current = false;
          setActivePoint(null);
        }
        lastMouseRef.current = { x: ex, y: ey, time: now };
      }

      if (!timerRef.current && !isVisibleRef.current) {
        // Cooldown gate — skip arming if last show was within COOLDOWN_MS.
        if (Date.now() - lastShownRef.current < COOLDOWN_MS) return;
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          // Re-check cooldown at fire time too.
          if (Date.now() - lastShownRef.current < COOLDOWN_MS) return;
          const snap = latestHoverRef.current;
          const chartInstance = chartRef.current?.getEchartsInstance?.();

          if (!snap || !chartInstance) return;

          const firstItem = snap.items?.[0];
          if (!firstItem) return;

          const anchor = lastMouseRef.current;
          let pointX = anchor?.x ?? ex;
          let pointY = anchor?.y ?? ey;

          try {
            const option = chartInstance.getOption();
            const series = Array.isArray(option?.series) ? option.series : [];
            const seriesIndex = series.findIndex((s: any) => s.name === firstItem.seriesName);
            if (seriesIndex !== -1) {
              const pixel = chartInstance.convertToPixel(
                { seriesIndex, xAxisIndex: 0 },
                [snap.dataIndex, firstItem.value]
              );
              if (pixel && pixel.length === 2 && !isNaN(pixel[0]) && !isNaN(pixel[1])) {
                pointX = pixel[0];
                pointY = pixel[1];
              }
            }
          } catch (err) {}

          setActivePoint({ x: pointX, y: pointY, dataIndex: snap.dataIndex });
          setIsVisible(true);
          isVisibleRef.current = true;

          // Bump persistent counter; auto-graduate to "learned" once user has
          // seen the tip MAX_SHOWS times. Stamp last-shown for cooldown.
          const nextCount = showCountRef.current + 1;
          showCountRef.current = nextCount;
          writeShowCount(nextCount);
          const nowTs = Date.now();
          lastShownRef.current = nowTs;
          writeLastShown(nowTs);

          if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
          hideTimerRef.current = setTimeout(() => {
            hideTimerRef.current = null;
            setIsVisible(false);
            isVisibleRef.current = false;
            setActivePoint(null);
            if (showCountRef.current >= MAX_SHOWS) {
              setLearned(true);
            }
          }, 3500);
        }, 700);
      }
    };

    const onLeave = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      setIsVisible(false);
      isVisibleRef.current = false;
      setActivePoint(null);
      lastMouseRef.current = null;
    };

    wrap.addEventListener("mousemove", onMove);
    wrap.addEventListener("mouseleave", onLeave);

    return () => {
      wrap.removeEventListener("mousemove", onMove);
      wrap.removeEventListener("mouseleave", onLeave);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [learned, isCompareModeActive, chartWrapRef, chartRef, latestHoverRef]);

  return {
    learned,
    isVisible,
    activePoint,
    cancelOnboarding: clearOnboarding,
    markLearned,
  };
}
